import React, { useState, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
import { Button, Tooltip, InputAdornment, useTheme, TextField } from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import SendIcon from '@mui/icons-material/Send';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';
import styles from '../../components/QuestionInput/QuestionInput.module.css';
import styled from '@emotion/styled';
import Chip from '@mui/material/Chip';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { FONT_PRIMARY } from '../../theme/typography';
import ModelSelectionDropdown from '../KnowledgeAI/ModelSelectionDropdown';
import FileUploadInput, { UploadedBlobFile } from '../FileUpload/FileUploadInput';

const HighlightOnHover = styled('div')({
  '&:hover .highlightIcon': {
    position: 'relative',
    boxShadow: '0 0 10px 5px rgba(169, 169, 169, 0.7)',
    borderRadius: '5px',
    zIndex: '1',
    transition: 'box-shadow 0.3s ease-in-out',
  },
});

/** Default LLM model options (aligned with Settings / env defaults). */
const StyledTextField = styled(TextField)({
  '&. MuiTouchRipple-root': {
    display: 'none',
  },
});

type VoiceInputComponentProps = {
  handleSendChat: (text?: string, retry?: boolean, filePath?: string | null) => void | Promise<void>;
  /** Controlled selected model deployment string (optional). */
  selectedModel?: string;
  /** Called when user picks a model. Passes the deployment string. */
  onModelChange?: (model: string) => void;
  /** Whether to show the model selector dropdown. */
  showModelSelector?: boolean;
  /** Username for scoping uploaded blobs under a user folder. */
  userName?: string;
  /** Whether to show the file upload button. */
  showFileUpload?: boolean;
};

//@ts-ignore
const VoiceInputComponent = ({
  handleSendChat,
  selectedModel: selectedModelProp,
  onModelChange,
  showModelSelector = false,
  userName = '',
  showFileUpload = false,
}: VoiceInputComponentProps) => {
  const theme = useTheme();
  const [question, setQuestion] = useState('');
  const { transcript, resetTranscript, listening } = useSpeechRecognition();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [attachedFile, setAttachedFile] = useState(null);
  const [error, setError] = useState<any>(null);

  // Blob-upload file state
  const [blobFile, setBlobFile] = useState<UploadedBlobFile | null>(null);
  const defaultModel = 'gpt-4.1';
  const [internalModel, setInternalModel] = useState(defaultModel);
  const selectedModel = selectedModelProp ?? internalModel;

  const handleModelSelect = (value: string) => {
    if (onModelChange) {
      onModelChange(value);
    } else {
      setInternalModel(value);
    }
  };
  const enableMic = process.env.REACT_APP_ENABLE_MIC || 'true';
  const handleStartListening = () => {
    resetTranscript(); // Clear previous transcript
    SpeechRecognition.startListening();
    setQuestion(''); // Clear previous question if necessary
  };

  const handleStopListening = () => {
    SpeechRecognition.stopListening();
    setQuestion(transcript); // Use the transcript as the new question
  };

  const onTextFieldChange = (e: any) => {
    setQuestion(e.target.value);
  };

  const handleUploadSuccess = (file: UploadedBlobFile) => {
    setBlobFile(file);
  };

  const handleDeleteBlobFile = () => {
    setBlobFile(null);
  };

  const handleVoice = async () => {
    const text = question || transcript;
    if (!text) return;
    const filePath = blobFile?.filePath ?? null;
    setQuestion(''); // Clear the question after sending
    resetTranscript(); // Clear the transcript after sending
    setBlobFile(null); // Clear the blob-uploaded file after sending
    setAttachedFile(null); // Clear the local attached file after sending
    await handleSendChat(text, undefined, filePath);
  };

  const handleIconClick = () => {
    imageInputRef?.current?.click(); // Trigger the image input click
  };

  const handleImageChange = (e: any) => {
    const file = e.target.files[0];
    // Handle the image file here
    console.log('Image file selected:', file);
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 20 * 1024 * 1024; // 20MB in bytes
      if (file.size > maxSize) {
        setError('File size exceeds 20MB. Please choose a smaller file.');
      } else {
        setAttachedFile(file);
        setError(null);
      }
    }
    // Handle the document or audio file here
    console.log('File selected:', file);
  };

  const handleCloseSnackbar = () => {
    setError(null);
  };

  const handleFileRemove = () => {
    setAttachedFile(null);
  };

  const textFieldStyles = attachedFile ? { paddingTop: '32px' } : {};

  const uploadHandler = () => {
    fileInputRef?.current?.click(); // Trigger the file input click
  };

  return (
    <div>
      {/* Existing local-file attachment chip (commented upload workflow) */}
      {attachedFile && (
        <div style={{ marginTop: '-60px', paddingBottom: '8px', paddingLeft: '8px' }}>
          <Chip
            icon={<InsertDriveFileIcon />}
            // @ts-ignore
            label={attachedFile.name}
            onDelete={handleFileRemove}
            style={{ marginLeft: '8px', maxWidth: 'calc(100% - 48px)' }}
          />
        </div>
      )}
      {/* Blob-uploaded file card — shown above the text input */}
      {blobFile && (
        <div style={{ marginBottom: '8px', paddingLeft: '4px' }}>
          <Chip
            icon={<InsertDriveFileIcon />}
            label={blobFile.fileName}
            onDelete={handleDeleteBlobFile}
            color="primary"
            variant="outlined"
            style={{ maxWidth: 'calc(100% - 16px)' }}
          />
        </div>
      )}
      <StyledTextField
        fullWidth
        placeholder={attachedFile ? 'Type your query here...' : 'Type your query here...'}
        onChange={onTextFieldChange}
        value={question || transcript} // Prioritize question, fallback to transcript if empty
        multiline={attachedFile || true}
        maxRows={2}
        minRows={1}
        className="search-input"
        onKeyDown={(e: any) => {
          if (e.key === 'Enter' && (e.altKey || e.shiftKey || e.ctrlKey)) {
            e.preventDefault();
            setQuestion((prev) => prev + '\n'); // Allow multiline with Enter + modifier key
          } else if (e.key === 'Enter') {
            e.preventDefault();
            handleVoice(); // Send chat on Enter
          }
        }}
        InputProps={{
          startAdornment: showFileUpload ? (
            <div style={{ display: 'flex', alignItems: 'center', marginRight: 4 }}>
              <FileUploadInput userName={userName} onUploadSuccess={handleUploadSuccess} disabled={!!blobFile} />
            </div>
          ) : undefined,
          endAdornment: (
            <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 4 }}>
              {showModelSelector && (
                <ModelSelectionDropdown selectedModel={selectedModel} onModelChange={handleModelSelect} />
              )}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {/* {enableMic=="true" ?(
                    <HighlightOnHover>
                      <Tooltip title={listening ? 'Stop Listening' : 'Start Listening'} placement="top">
                        <div
                          className={`${styles.questionInputSendButton}`}
                          aria-label="Voice Input"
                          onClick={listening ? handleStopListening : handleStartListening}
                        >
                          <MicIcon className='highlightIcon' />
                        </div>
                      </Tooltip>
                    </HighlightOnHover>
                  ):null
                } */}
                <Button
                  sx={{ height: '100%', borderRadius: '0px' }}
                  onClick={handleVoice}
                  disabled={!question && !transcript} // Disable if both question and transcript are empty
                >
                  <InputAdornment position="end">
                    <SendIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                </Button>
              </div>
              <input
                type="file"
                accept=".txt, .pdf, .docx, .wav, .mp3"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </div>
          ),
        }}
      />
      {/* Snackbar for displaying errors */}
      {error && (
        <Snackbar
          open={!!error}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="error">
            {error}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
};

export default VoiceInputComponent;
